import React, { useState, useEffect } from 'react';
import { Row, Col, Tag, notification, Popconfirm } from 'antd';
import { CheckCircleFilled, ClearOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import {
  ThemeButton,
  ThemeForm,
  ThemeInput,
  ThemeTableCard,
} from '../Styles/Theme';
import {
  updateAppVersionApi,
  updateAppVersionReset,
} from '../../store/actions/settingAction';
import { formatDateTime } from '../../utils/helpers';

const Settings = (props) => {
  const {
    versionData,
    updateAppVersionState,
    updateAppVersion,
    updateAppVersionReset,
  } = props;

  const [version, setVersion] = useState('');

  const currentVersion = versionData?.current || '';
  const history = versionData?.history || [];

  useEffect(() => {
    if (updateAppVersionState.apiState === 'success') {
      notification.success({ message: 'Version updated successfully' });
      setVersion('');
      updateAppVersionReset();
    } else if (updateAppVersionState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: updateAppVersionState.message || 'Failed to update version',
      });
      updateAppVersionReset();
    }
  }, [updateAppVersionState.apiState]);

  const handleVersionChange = (e) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    setVersion(val);
  };

  const handleSave = () => {
    const trimmed = version.trim();
    if (!trimmed) return;
    if (/^0+(\.0+)*$/.test(trimmed)) {
      notification.error({ message: 'Version cannot be all zeros' });
      return;
    }
    if (history.some(h => h.version === trimmed)) {
      notification.error({ message: 'This version already exists. Use "Set Current" from history.' });
      return;
    }
    updateAppVersion(trimmed);
  };

  const handleClear = () => {
    updateAppVersion(null, 'clear');
  };

  const handleSetCurrent = (originalIndex) => {
    updateAppVersion(originalIndex, 'set_current');
  };

  // Reverse history so newest is first, but keep original index for set_current
  const sortedHistory = history.map((item, i) => ({ ...item, originalIndex: i })).reverse();

  // Find the first entry in sorted list that matches current version (for "Current" tag)
  const currentTagIndex = currentVersion
    ? sortedHistory.findIndex((item) => item.version === currentVersion)
    : -1;

  const isLoading = updateAppVersionState.apiState === 'loading';

  return (
    <div style={{ padding: '24px' }}>
      <ThemeTableCard title="Version Number" style={{ maxWidth: 520 }}>
        <div style={{ padding: '16px 20px' }}>
          {/* Input Row */}
          <Row gutter={12} align="middle">
            <Col flex="auto">
              <ThemeForm.Item label="New Version" style={{ marginBottom: 0 }}>
                <ThemeInput
                  value={version}
                  onChange={handleVersionChange}
                  placeholder="e.g. 10.1"
                  maxLength={20}
                  onPressEnter={handleSave}
                />
              </ThemeForm.Item>
            </Col>
            <Col>
              <ThemeButton
                type="primary"
                onClick={handleSave}
                loading={isLoading}
                disabled={!version.trim()}
                style={{ marginTop: 4 }}
              >
                Save
              </ThemeButton>
            </Col>
          </Row>

          {/* Current + Clear */}
          {currentVersion && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: '#83899F' }}>
                Current: <strong style={{ color: '#0A1B39' }}>Version {currentVersion}</strong>
              </span>
              <Popconfirm
                title="Clear version from sidebar?"
                onConfirm={handleClear}
                okText="Yes"
                cancelText="No"
                placement="left"
              >
                <ThemeButton type="default" size="small" icon={<ClearOutlined />} style={{ fontSize: 12 }}>
                  Clear
                </ThemeButton>
              </Popconfirm>
            </div>
          )}

          {/* History */}
          {sortedHistory.length > 0 && (
            <div style={{ marginTop: 16, borderTop: '1px solid #E2E8EE', paddingTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0A1B39', marginBottom: 8 }}>History</div>
              <div style={{ maxHeight: 240, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#d9d9d9 #f5f5f5' }}>
                {sortedHistory.map((item, index) => {
                  const isCurrent = index === currentTagIndex;
                  return (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 10px',
                        borderRadius: 6,
                        marginBottom: 4,
                        background: isCurrent ? '#F4F6F9' : 'transparent',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#0A1B39' }}>
                          {item.version}
                        </span>
                        {isCurrent && (
                          <Tag color="purple" style={{ margin: 0, fontSize: 10, lineHeight: '18px', borderRadius: 4 }}>
                            <CheckCircleFilled style={{ marginRight: 3 }} />
                            Current
                          </Tag>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, color: '#83899F' }}>
                          {item.created_at ? formatDateTime(item.created_at) : ''}
                        </span>
                        {!isCurrent && (
                          <a
                            onClick={() => handleSetCurrent(item.originalIndex)}
                            style={{ fontSize: 11, color: '#5F42DE', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            Set Current
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </ThemeTableCard>
    </div>
  );
};

const mapStateToProps = (state) => ({
  versionData: state.setting?.getAppVersionState?.data?.value || null,
  updateAppVersionState: state.setting?.updateAppVersionState || { apiState: 'idle' },
});

const mapDispatchToProps = (dispatch) => ({
  updateAppVersion: (value, action) => dispatch(updateAppVersionApi(value, action)),
  updateAppVersionReset: () => dispatch(updateAppVersionReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
